import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "../GameFullscreenButton";
import { ChevronDown, Pencil } from "lucide-react";
import { CRITTERS, DOLOPALS, MISSIONS, seededRand } from "../../data/dolopals";
import { downloadRadarCard, downloadRecapCard } from "../../utils/shareCard";
import { toast } from "../ui/sonner";
import { RADAR_SKINS, critterOfTheWeek, isBossEve, isBossFriday, isShinySunday, radarDayKey, shinyOdds, signalOfTheDay, treasureFor, weekKeyNow } from "./utils";
import { catchSfx, critterCry, critterSfx, feverSfx, jamSfx, pingSfx, scareSfx, unlockRadarAudio, waveSfx } from "./audio";

const PAL_COLORS = {
  bruin: "#ffcf5c",
  riff: "#00c8ff",
  mia: "#ff5cd0",
  socket: "#9dff57",
  blackout: "#ff3b30",
  killswitch: "#ff6a00",
  woofer: "#b28bff",
  kernel: "#2affd5",
  slurp: "#f0ece9",
};

const RADAR_POS = {
  mine: { angle: -2.1, dist: 0.8 },
  market: { angle: -0.45, dist: 0.62 },
  decrypt: { angle: 0.95, dist: 0.7 },
  tower: { angle: 2.45, dist: 0.85 },
};

// live expedition radar: a digital maze dish where colored blips track every titan

const WAVE_MOODS = ["WAVED BACK", "IS HUNGRY FOR HUGS", "HEARD THAT", "SAYS SQUEAK", "IS DOING THEIR BEST", "BLINKED TWICE"];

// local YYYY-MM-DD (shared by the daily treasure cache)

export function TitanRadar({ exp, collection, awardShards, slowRun, bumpCaught, saveDefense, claimTreasure, collectCritter, saveOutpost, bumpMegaKills }) {
  const ref = useRef(null);
  const [pingOn, setPingOn] = useState(false); // sonar pings start OFF — kids opt in
  const pingRef = useRef(false);
  const prevDRef = useRef({});
  // ---------- radar mini-games: SIGNAL TAP + WORM CONTACTS ----------
  const spikesRef = useRef([]); // gold spikes: {id, ang, dist, born, ttl}
  const wormsRef = useRef([]); // red contacts: {id, targetId, ang, dist, rate, dead}
  const textsRef = useRef([]); // floating rewards: {txt, x, y, born, color}
  const comboRef = useRef(0);
  const shieldUsedRef = useRef(false); // COMBO SHIELD absorbs one faded spike per chain
  const nextSpikeRef = useRef(0);
  const nextWormRef = useRef(0);
  const lastFrameRef = useRef(0);
  const idRef = useRef(0);
  const expRef = useRef(exp);
  expRef.current = exp;
  const [gameStats, setGameStats] = useState({ caught: 0, combo: 0, best: 0, worms: 0 });
  const [rulesOpen, setRulesOpen] = useState(false);
  // ---------- DEFENSE MODE — 60-second worm rush on HQ ----------
  const defenseRef = useRef(null); // {startT, score, nextWorm, lastTick} while live
  const [defense, setDefense] = useState(null); // {score, secsLeft} live | {score, best, record, done} report
  const [weeklyChamp, setWeeklyChamp] = useState(null); // {tag, score}
  // fever: ×5 combo ignites a 10s double-pay rainbow rush
  const feverRef = useRef({ until: 0 });
  const feverStateRef = useRef(false);
  const [feverOn, setFeverOn] = useState(false);
  // scrap critters: one wanders the grid every so often, tap to adopt
  const critterRef = useRef(null); // {id, ang, dist, born, phase, shiny}
  const nextCritterRef = useRef(0);
  const forceShinyRef = useRef(false); // QA handle: next spawn is shiny
  // MEGA WORM: rare segmented boss blip — three taps to kill, +200 shards
  const megaWormRef = useRef(null); // {ang, dist, hp, born, lastHit}
  const nextMegaRef = useRef(0);
  const megaStateRef = useRef(false);
  const [megaOn, setMegaOn] = useState(false);
  // titan blip positions recorded per frame so taps can find them (wave-back)
  const blipsRef = useRef([]);
  // outpost rename
  const [outpostEdit, setOutpostEdit] = useState(false);
  const [outpostName, setOutpostName] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewards/defense`)
      .then((r) => r.json())
      .then((d) => setWeeklyChamp(d.leader || null))
      .catch(() => {});
  }, []);

  const startDefense = () => {
    if (defenseRef.current) return;
    spikesRef.current = [];
    wormsRef.current = [];
    comboRef.current = 0;
    defenseRef.current = { startT: performance.now(), score: 0, nextWorm: 0, lastTick: 0 };
    setDefense({ score: 0, secsLeft: 60, done: false });
  };

  const endDefense = (finalScore) => {
    defenseRef.current = null;
    (async () => {
      let info = { best: finalScore, record: finalScore > 0, leader: null };
      try {
        info = { ...info, ...((await saveDefense?.(finalScore)) || {}) };
      } catch {}
      setDefense({ score: finalScore, best: info.best, record: info.record, done: true });
      if (info.leader) setWeeklyChamp(info.leader);
      jamSfx();
    })();
  };

  // rainbow sparkle burst when an ULTRA spike is caught
  const ultraBurst = (x, y, born) => {
    for (let k = 0; k < 6; k += 1) {
      textsRef.current.push({
        txt: "✦",
        x: x + Math.cos((k / 6) * Math.PI * 2) * 30,
        y: y + Math.sin((k / 6) * Math.PI * 2) * 30,
        born,
        color: `hsl(${k * 60}, 100%, 70%)`,
      });
    }
  };

  // radar-relative polar -> CSS px on the page (used by the tap handler + QA handle)
  const toCss = (ang, dist) => {
    const canvas = ref.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const c = rect.width / 2;
    const rr = rect.width * 0.42;
    return { x: rect.left + c + Math.cos(ang) * rr * dist, y: rect.top + c + Math.sin(ang) * rr * dist };
  };

  const tapRadar = (event) => {
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const x = (event.clientX - rect.left) * dpr;
    const y = (event.clientY - rect.top) * dpr;
    const W = canvas.width;
    const C = W / 2;
    const R = W * 0.42;
    const hitR = W * 0.055;
    const nowT = performance.now();
    // MEGA WORM first — the raid boss is the most urgent thing on the grid
    const mg = megaWormRef.current;
    if (mg && nowT - mg.lastHit > 150) {
      const mx = C + Math.cos(mg.ang) * R * mg.dist;
      const my = C + Math.sin(mg.ang) * R * mg.dist;
      if (Math.hypot(mx - x, my - y) < W * 0.06) {
        mg.lastHit = nowT;
        mg.hp -= 1;
        if (mg.hp <= 0) {
          megaWormRef.current = null;
          awardShards?.(200);
          bumpMegaKills?.();
          toast.success("MEGA WORM DOWN — +200 SCRAP-SHARDS.");
          feverSfx();
          ultraBurst(x, y, nowT);
          textsRef.current.push({ txt: "+200", x, y, born: nowT, color: "#ff3b30" });
        } else {
          scareSfx();
          mg.dist = Math.min(0.95, mg.dist + 0.05); // knockback
          textsRef.current.push({ txt: mg.hp === 2 ? "2 MORE!" : "1 MORE!", x, y, born: nowT, color: "#ff5c7a" });
        }
        return;
      }
    }
    // worms take priority — they threaten the runners
    const worm = wormsRef.current.find((w) => !w.dead && Math.hypot(C + Math.cos(w.ang) * R * w.dist - x, C + Math.sin(w.ang) * R * w.dist - y) < hitR);
    if (worm) {
      worm.dead = true;
      awardShards?.(1);
      scareSfx();
      textsRef.current.push({ txt: "+1", x, y, born: nowT, color: "#ff5c7a" });
      if (worm.defense && defenseRef.current) {
        defenseRef.current.score += 1;
        setDefense((d) => (d && !d.done ? { ...d, score: defenseRef.current.score } : d));
      }
      setGameStats((s) => ({ ...s, worms: s.worms + 1 }));
      return;
    }
    const spIdx = spikesRef.current.findIndex((sp) => Math.hypot(C + Math.cos(sp.ang) * R * sp.dist - x, C + Math.sin(sp.ang) * R * sp.dist - y) < hitR);
    if (spIdx >= 0) {
      const sp = spikesRef.current[spIdx];
      spikesRef.current.splice(spIdx, 1);
      if (comboRef.current === 0) shieldUsedRef.current = false; // re-arm the shield on a fresh chain
      comboRef.current += 1;
      // a ×5 chain ignites FEVER MODE — ten seconds of double pay
      if (comboRef.current === 5 && nowT >= feverRef.current.until) {
        feverRef.current.until = nowT + 10000;
        feverSfx();
        textsRef.current.push({ txt: "FEVER ×2!", x: C, y: C - R * 0.5, born: nowT, color: "#ffdf6b" });
      }
      const fever = nowT < feverRef.current.until ? 2 : 1;
      const pay = (sp.base || 2) * Math.min(comboRef.current, 5) * fever;
      awardShards?.(pay);
      bumpCaught?.();
      catchSfx();
      if (sp.tier === "ultra") ultraBurst(x, y, nowT);
      const color = sp.tier === "ultra" ? "#ff9df5" : sp.tier === "gold" ? "#ffdf6b" : "#e8e6e3";
      textsRef.current.push({ txt: `+${pay}`, x, y, born: nowT, color });
      setGameStats((s) => ({ ...s, caught: s.caught + 1, best: Math.max(s.best, comboRef.current), combo: comboRef.current }));
      return;
    }
    // scrap critter adoption
    const crit = critterRef.current;
    if (crit && Math.hypot(C + Math.cos(crit.ang) * R * crit.dist - x, C + Math.sin(crit.ang) * R * crit.dist - y) < hitR) {
      collectCritter?.(crit.id, crit.shiny);
      critterCry(crit.id);
      if (crit.shiny) {
        feverSfx();
        ultraBurst(x, y, nowT);
        textsRef.current.push({ txt: "SHINY!", x, y: y - W * 0.03, born: nowT, color: "#ffd700" });
      } else {
        critterSfx();
      }
      textsRef.current.push({ txt: "+CRITTER", x, y, born: nowT, color: crit.shiny ? "#ffd700" : "#3dff7a" });
      critterRef.current = null;
      return;
    }
    // titan wave-back — tap your own runner's blip
    const blip = blipsRef.current.find((b) => Math.hypot(b.bx - x, b.by - y) < hitR);
    if (blip) {
      const pal = DOLOPALS.find((p) => p.id === blip.id);
      const mood = WAVE_MOODS[Math.floor(Math.random() * WAVE_MOODS.length)];
      waveSfx();
      textsRef.current.push({ txt: "♥", x, y: y - W * 0.02, born: nowT, color: "#ff5c7a" });
      textsRef.current.push({ txt: `${pal ? pal.name : "TITAN"} ${mood}`, x, y: y + W * 0.045, born: nowT, color: "#e8e6e3" });
      return;
    }
    // treasure ping — proximity sonar toward today's buried cache (skipped during defense)
    if (!defenseRef.current && expRef.current.treasure !== radarDayKey(new Date())) {
      const cache = treasureFor(radarDayKey(new Date()));
      const cd = Math.hypot(C + Math.cos(cache.ang) * R * cache.dist - x, C + Math.sin(cache.ang) * R * cache.dist - y);
      if (cd < hitR) {
        claimTreasure?.();
        feverSfx();
        ultraBurst(x, y, nowT);
        textsRef.current.push({ txt: "CACHE CRACKED +50", x, y, born: nowT, color: "#ffdf6b" });
      } else {
        const prox = cd / R;
        const label = prox < 0.18 ? "BLAZING" : prox < 0.35 ? "HOT" : prox < 0.55 ? "WARM" : prox < 0.8 ? "COLD" : "ICE COLD";
        const color = prox < 0.18 ? "#ff3b30" : prox < 0.35 ? "#ff9900" : prox < 0.55 ? "#ffdf6b" : prox < 0.8 ? "#6ee7ff" : "#8a9aa8";
        textsRef.current.push({ txt: label, x, y, born: nowT, color });
        pingSfx();
      }
    }
  };
  const togglePing = () =>
    setPingOn((v) => {
      const next = !v;
      pingRef.current = next;
      return next;
    });
  const owned = DOLOPALS.filter((p) => collection.includes(p.id));

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const size = canvas.clientWidth || 320;
      if (canvas.width !== Math.round(size * dpr)) {
        canvas.width = Math.round(size * dpr);
        canvas.height = Math.round(size * dpr);
      }
      const W = canvas.width;
      const C = W / 2;
      const R = W * 0.42;
      const t = performance.now();
      const nowTs = Date.now();
      const dtMs = lastFrameRef.current ? Math.min(64, t - lastFrameRef.current) : 16.7;
      lastFrameRef.current = t;
      const sotdId = signalOfTheDay();
      const skin = RADAR_SKINS[expRef.current.skin] || RADAR_SKINS.default;
      const G = (a) => `rgba(${skin.line}, ${a})`;
      const feverOn = t < feverRef.current.until;
      if (feverOn !== feverStateRef.current) {
        feverStateRef.current = feverOn;
        setFeverOn(feverOn);
      }
      ctx.clearRect(0, 0, W, W);
      ctx.fillStyle = "#020503";
      ctx.beginPath();
      ctx.arc(C, C, R + W * 0.02, 0, Math.PI * 2);
      ctx.fill();
      ctx.save();
      ctx.beginPath();
      ctx.arc(C, C, R, 0, Math.PI * 2);
      ctx.clip();
      // digital maze walls (deterministic)
      const step = W / 16;
      const mz = seededRand("radar-maze");
      ctx.strokeStyle = G(0.07);
      ctx.lineWidth = Math.max(1, W / 640);
      for (let gx = 0; gx < 16; gx += 1) {
        for (let gy = 0; gy < 16; gy += 1) {
          const x0 = gx * step;
          const y0 = gy * step;
          const v = mz();
          ctx.beginPath();
          if (v < 0.35) {
            ctx.moveTo(x0, y0); ctx.lineTo(x0 + step, y0);
          } else if (v < 0.7) {
            ctx.moveTo(x0, y0); ctx.lineTo(x0, y0 + step);
          } else {
            ctx.moveTo(x0, y0); ctx.lineTo(x0 + step, y0);
            ctx.moveTo(x0, y0); ctx.lineTo(x0, y0 + step);
          }
          ctx.stroke();
        }
      }
      // range rings + spokes
      [0.33, 0.66, 1].forEach((f) => {
        ctx.beginPath();
        ctx.arc(C, C, R * f, 0, Math.PI * 2);
        ctx.strokeStyle = G(0.14);
        ctx.stroke();
      });
      for (let i = 0; i < 8; i += 1) {
        const a = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(C, C);
        ctx.lineTo(C + Math.cos(a) * R, C + Math.sin(a) * R);
        ctx.strokeStyle = G(0.06);
        ctx.stroke();
      }
      // rotating sweep with fading trail (manual slices — works on older browsers)
      const sweep = (t / 2600) % (Math.PI * 2);
      for (let i = 0; i < 36; i += 1) {
        const a0 = sweep - (i / 36) * 1.1;
        ctx.beginPath();
        ctx.moveTo(C, C);
        ctx.arc(C, C, R, a0 - 0.035, a0);
        ctx.closePath();
        ctx.fillStyle = feverOn
          ? `hsla(${Math.floor(t / 2 + i * 10) % 360}, 100%, 62%, ${(0.2 * (1 - i / 36)).toFixed(3)})`
          : G((0.16 * (1 - i / 36)).toFixed(3));
        ctx.fill();
      }
      // mission zones
      Object.entries(RADAR_POS).forEach(([mid, pos]) => {
        const m = MISSIONS.find((x) => x.id === mid);
        const zx = C + Math.cos(pos.angle) * R * pos.dist;
        const zy = C + Math.sin(pos.angle) * R * pos.dist;
        const s = W * 0.016;
        ctx.save();
        ctx.translate(zx, zy);
        ctx.rotate(Math.PI / 4);
        ctx.strokeStyle = "rgba(255, 153, 0, 0.75)";
        ctx.lineWidth = Math.max(1.5, W / 400);
        ctx.strokeRect(-s, -s, s * 2, s * 2);
        ctx.restore();
        ctx.fillStyle = "rgba(255, 153, 0, 0.8)";
        ctx.font = `900 ${Math.round(W * 0.026)}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText(m.name.split(" ")[0].toUpperCase(), zx, zy - W * 0.035);
        // SIGNAL OF THE DAY — gold pulse ring + 2X tag on the double-pay zone
        if (mid === sotdId) {
          ctx.beginPath();
          ctx.arc(zx, zy, s * (2.1 + 0.35 * Math.sin(t / 200)), 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(255, 223, 107, 0.85)";
          ctx.lineWidth = Math.max(1.5, W / 420);
          ctx.shadowColor = "#ffdf6b";
          ctx.shadowBlur = 10;
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.fillStyle = "#ffdf6b";
          ctx.fillText("2X", zx, zy + W * 0.045);
        }
      });
      // titan blips
      const frameBlips = [];
      owned.forEach((pal, idx) => {
        const color = PAL_COLORS[pal.id] || "#ffcf5c";
        const run = exp.active[pal.id];
        let bx;
        let by;
        let working = false;
        let readyPulse = false;
        if (run) {
          const m = MISSIONS.find((x) => x.id === run.missionId);
          const total = m.seconds * 1000;
          const p = Math.max(0, Math.min(1, 1 - (run.endsAt - nowTs) / total));
          const pos = RADAR_POS[run.missionId];
          const zx = C + Math.cos(pos.angle) * R * pos.dist;
          const zy = C + Math.sin(pos.angle) * R * pos.dist;
          if (p >= 1) {
            const a = idx * 1.3;
            bx = C + Math.cos(a) * R * 0.12;
            by = C + Math.sin(a) * R * 0.12;
            readyPulse = true;
          } else if (p < 0.25) {
            const q = p / 0.25;
            bx = C + (zx - C) * q;
            by = C + (zy - C) * q;
          } else if (p > 0.82) {
            const q = (p - 0.82) / 0.18;
            bx = zx + (C - zx) * q;
            by = zy + (C - zy) * q;
          } else {
            bx = zx;
            by = zy;
            working = true;
          }
        } else {
          const a = (idx / Math.max(1, owned.length)) * Math.PI * 2 - Math.PI / 2;
          bx = C + Math.cos(a) * R * 0.16;
          by = C + Math.sin(a) * R * 0.16;
        }
        // record the blip so taps can find it (wave-back)
        frameBlips.push({ id: pal.id, bx, by });
        // blips flare when the sweep passes over them
        const blipAngle = Math.atan2(by - C, bx - C);
        let d = (sweep - blipAngle) % (Math.PI * 2);
        if (d < 0) d += Math.PI * 2;
        const glow = d < 1.2 ? 1 - d / 1.2 : 0;
        // sonar ping the moment the sweep crosses this blip
        const prevD = prevDRef.current[pal.id];
        if (pingRef.current && prevD !== undefined && prevD > 4 && d < 2) pingSfx();
        prevDRef.current[pal.id] = d;
        const pr = (working || readyPulse ? 1 + 0.3 * Math.sin(t / 180) : 1) * W * 0.014;
        ctx.save();
        ctx.shadowColor = color;
        ctx.shadowBlur = 8 + glow * 18;
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.55 + glow * 0.45;
        ctx.beginPath();
        ctx.arc(bx, by, pr, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (readyPulse) {
          ctx.beginPath();
          ctx.arc(bx, by, pr * (2 + Math.sin(t / 220)), 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(255, 153, 0, 0.8)";
          ctx.lineWidth = Math.max(1, W / 500);
          ctx.stroke();
        }
      });
      blipsRef.current = frameBlips;
      // ---------- SIGNAL TAP + WORM CONTACTS layer ----------
      const activeRuns = expRef.current.active || {};
      const workingIds = Object.keys(activeRuns).filter((pid) => activeRuns[pid].endsAt > nowTs);
      const ownedUpgrades = expRef.current.owned || [];
      // ---------- DEFENSE MODE — worms rush HQ for 60 seconds ----------
      const def = defenseRef.current;
      if (def) {
        const elapsed = t - def.startT;
        if (elapsed >= 60000) {
          endDefense(def.score);
        } else {
          const interval = 1400 - (elapsed / 60000) * 800; // escalating rush
          if (t >= def.nextWorm && wormsRef.current.length < 5) {
            wormsRef.current.push({ id: (idRef.current += 1), targetId: null, ang: Math.random() * Math.PI * 2, dist: 1.02, rate: 1.02 / 6000, dead: false, defense: true });
            def.nextWorm = t + interval;
          }
          if (t - def.lastTick > 250) {
            def.lastTick = t;
            setDefense((d) => (d && !d.done ? { ...d, score: def.score, secsLeft: Math.ceil((60000 - elapsed) / 1000) } : d));
          }
        }
      }
      // spawn signal spikes (max 2 on the grid) — silver common, gold rich, rainbow ULTRA rare
      if (!def && t >= nextSpikeRef.current && spikesRef.current.length < 2) {
        const roll = Math.random();
        const tier = roll < 0.7 ? "silver" : roll < 0.95 ? "gold" : "ultra";
        spikesRef.current.push({
          id: (idRef.current += 1),
          tier,
          base: tier === "ultra" ? 25 : tier === "gold" ? 5 : 2,
          ang: Math.random() * Math.PI * 2,
          dist: 0.22 + Math.random() * 0.68,
          born: t,
          ttl: ownedUpgrades.includes("upgrade-wider-dish") ? 5600 : 3600,
        });
        nextSpikeRef.current = t + 3800 + Math.random() * 3200;
      }
      // scrap critters wander in every 45-90s (paused during defense), linger 15s — 8% spawn SHINY gold, tripled on SHINY SUNDAY
      // the CRITTER OF THE WEEK wanders in far more often (40% of visits)
      if (!def && !critterRef.current && t >= nextCritterRef.current) {
        critterRef.current = {
          id: Math.random() < 0.4 ? critterOfTheWeek().id : CRITTERS[Math.floor(Math.random() * CRITTERS.length)].id,
          ang: Math.random() * Math.PI * 2,
          dist: 0.3 + Math.random() * 0.55,
          born: t,
          phase: Math.random() * Math.PI * 2,
          shiny: forceShinyRef.current || Math.random() < shinyOdds(),
        };
        forceShinyRef.current = false;
        nextCritterRef.current = t + 45000 + Math.random() * 45000;
      }
      if (critterRef.current && t - critterRef.current.born > 15000) critterRef.current = null; // wandered off
      // MEGA WORM raid boss — rare segmented blip, three hits pays +200; BOSS FRIDAY doubles the surge
      if (!def && !megaWormRef.current && t >= nextMegaRef.current) {
        megaWormRef.current = { ang: Math.random() * Math.PI * 2, dist: 0.35 + Math.random() * 0.5, hp: 3, born: t, lastHit: 0 };
        nextMegaRef.current = t + (isBossFriday() ? 60000 + Math.random() * 45000 : 120000 + Math.random() * 90000);
      }
      if (megaWormRef.current && t - megaWormRef.current.born > (isBossFriday() ? 30000 : 22000)) megaWormRef.current = null; // burrowed away
      const megaLive = Boolean(megaWormRef.current);
      if (megaLive !== megaStateRef.current) {
        megaStateRef.current = megaLive;
        setMegaOn(megaLive);
      }
      // spawn worm contacts only while a runner is out on a mission
      if (!def && workingIds.length && t >= nextWormRef.current && wormsRef.current.length < 2) {
        const targetId = workingIds[Math.floor(Math.random() * workingIds.length)];
        const zpos = RADAR_POS[activeRuns[targetId].missionId];
        if (zpos) {
          wormsRef.current.push({
            id: (idRef.current += 1),
            targetId,
            ang: Math.random() * Math.PI * 2,
            dist: 1.02,
            rate: ((1.02 - zpos.dist) / 26000) * (ownedUpgrades.includes("upgrade-worm-repellent") ? 0.6 : 1), // ~26s crawl
            dead: false,
          });
          nextWormRef.current = t + 14000 + Math.random() * 9000;
        } else {
          nextWormRef.current = t + 6000;
        }
      }
      // expired spikes break the combo — COMBO SHIELD absorbs one fade per chain
      spikesRef.current = spikesRef.current.filter((sp) => {
        if (t - sp.born > sp.ttl) {
          if (comboRef.current > 0) {
            if (ownedUpgrades.includes("upgrade-combo-shield") && !shieldUsedRef.current) {
              shieldUsedRef.current = true;
              textsRef.current.push({ txt: "SHIELD HELD", x: C, y: C - R * 0.3, born: t, color: "#00c8ff" });
            } else {
              comboRef.current = 0;
              setGameStats((s) => ({ ...s, combo: 0 }));
            }
          }
          return false;
        }
        return true;
      });
      // worms home in — defense fangs beeline for HQ, normal ones stalk the target zone
      wormsRef.current.forEach((wm) => {
        if (wm.dead) return;
        if (wm.defense) {
          wm.dist -= wm.rate * dtMs;
          if (wm.dist <= 0.05) wm.dead = true; // slipped through the line — no penalty, kid mode
          return;
        }
        const run = expRef.current.active[wm.targetId];
        const zpos = run ? RADAR_POS[run.missionId] : null;
        if (!zpos) {
          wm.dead = true;
          return;
        }
        let da = (zpos.angle - wm.ang) % (Math.PI * 2);
        if (da > Math.PI) da -= Math.PI * 2;
        if (da < -Math.PI) da += Math.PI * 2;
        wm.ang += da * 0.02 * (dtMs / 16.7);
        wm.dist -= wm.rate * dtMs;
        if (wm.dist <= zpos.dist + 0.02) {
          wm.dead = true;
          if (run.endsAt > nowTs) {
            slowRun?.(wm.targetId, 15000);
            jamSfx();
            textsRef.current.push({
              txt: "+15S JAM",
              x: C + Math.cos(zpos.angle) * R * zpos.dist,
              y: C + Math.sin(zpos.angle) * R * zpos.dist,
              born: t,
              color: "#ff3b30",
            });
          }
        }
      });
      wormsRef.current = wormsRef.current.filter((w) => !w.dead);
      // wandering scrap critter — green blob with ears; SHINY ones gleam gold with rays
      if (critterRef.current) {
        const cr = critterRef.current;
        cr.ang += Math.sin(t / 900 + cr.phase) * 0.00012 * dtMs;
        cr.dist += Math.cos(t / 1300 + cr.phase) * 0.00001 * dtMs;
        cr.dist = Math.max(0.18, Math.min(0.92, cr.dist));
        const cxp = C + Math.cos(cr.ang) * R * cr.dist;
        const cyp = C + Math.sin(cr.ang) * R * cr.dist;
        const cr2 = W * 0.014 * (1 + 0.2 * Math.sin(t / 200));
        const cc = cr.shiny ? "#ffd700" : "#3dff7a";
        ctx.save();
        ctx.shadowColor = cc;
        ctx.shadowBlur = cr.shiny ? 20 : 12;
        ctx.fillStyle = cr.shiny ? "rgba(255, 215, 0, 0.98)" : "rgba(61, 255, 122, 0.95)";
        ctx.beginPath();
        ctx.arc(cxp, cyp, cr2, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cxp - cr2 * 0.8, cyp - cr2 * 0.9, cr2 * 0.45, 0, Math.PI * 2);
        ctx.arc(cxp + cr2 * 0.8, cyp - cr2 * 0.9, cr2 * 0.45, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (cr.shiny) {
          const hue = Math.floor(t / 4) % 360;
          for (let k = 0; k < 4; k += 1) {
            const ra = (k / 4) * Math.PI * 2 + t / 280;
            ctx.beginPath();
            ctx.moveTo(cxp + Math.cos(ra) * cr2 * 1.8, cyp + Math.sin(ra) * cr2 * 1.8);
            ctx.lineTo(cxp + Math.cos(ra) * cr2 * 3, cyp + Math.sin(ra) * cr2 * 3);
            ctx.strokeStyle = `hsla(${(hue + k * 90) % 360}, 100%, 70%, 0.85)`;
            ctx.lineWidth = Math.max(1, W / 640);
            ctx.stroke();
          }
        }
      }
      // draw signal spikes by tier — silver small, gold rich, ULTRA cycling rainbow with rays
      spikesRef.current.forEach((sp) => {
        const sx = C + Math.cos(sp.ang) * R * sp.dist;
        const sy = C + Math.sin(sp.ang) * R * sp.dist;
        const left = 1 - (t - sp.born) / sp.ttl;
        const hue = Math.floor(t / 4) % 360;
        const color = sp.tier === "ultra" ? `hsl(${hue}, 100%, 65%)` : sp.tier === "gold" ? "#ffdf6b" : "#e8e6e3";
        const sr = W * (sp.tier === "ultra" ? 0.022 : sp.tier === "gold" ? 0.017 : 0.013) * (1 + 0.25 * Math.sin(t / 140));
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(Math.PI / 4);
        ctx.shadowColor = color;
        ctx.shadowBlur = sp.tier === "ultra" ? 22 : 14;
        ctx.fillStyle = color;
        ctx.globalAlpha = sp.tier === "silver" ? 0.9 : 0.95;
        ctx.fillRect(-sr, -sr, sr * 2, sr * 2);
        ctx.restore();
        ctx.beginPath();
        ctx.arc(sx, sy, W * 0.045 * (0.4 + 0.6 * left), 0, Math.PI * 2);
        ctx.strokeStyle = sp.tier === "ultra" ? `hsla(${hue}, 100%, 70%, ${(0.3 + 0.5 * left).toFixed(3)})` : sp.tier === "gold" ? `rgba(255, 223, 107, ${(0.25 + 0.55 * left).toFixed(3)})` : `rgba(232, 230, 227, ${(0.2 + 0.4 * left).toFixed(3)})`;
        ctx.lineWidth = Math.max(1, W / 500);
        ctx.stroke();
        if (sp.tier === "ultra") {
          for (let k = 0; k < 4; k += 1) {
            const ra = (k / 4) * Math.PI * 2 + t / 300;
            ctx.beginPath();
            ctx.moveTo(sx + Math.cos(ra) * sr * 2, sy + Math.sin(ra) * sr * 2);
            ctx.lineTo(sx + Math.cos(ra) * sr * 3.4, sy + Math.sin(ra) * sr * 3.4);
            ctx.strokeStyle = `hsla(${(hue + k * 90) % 360}, 100%, 70%, 0.8)`;
            ctx.lineWidth = Math.max(1, W / 640);
            ctx.stroke();
          }
        }
      });
      // draw worm contacts as red fangs crawling inward
      wormsRef.current.forEach((wm) => {
        const wx = C + Math.cos(wm.ang) * R * wm.dist;
        const wy = C + Math.sin(wm.ang) * R * wm.dist;
        const wr = W * 0.02;
        ctx.save();
        ctx.translate(wx, wy);
        ctx.rotate(wm.ang - Math.PI / 2);
        ctx.shadowColor = "#ff3b30";
        ctx.shadowBlur = 12 + 6 * Math.sin(t / 160);
        ctx.fillStyle = "rgba(255, 59, 48, 0.95)";
        ctx.beginPath();
        ctx.moveTo(0, -wr * 1.7);
        ctx.lineTo(wr, wr);
        ctx.lineTo(-wr, wr);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });
      // MEGA WORM — segmented red body; its length literally is its health
      if (megaWormRef.current) {
        const mg2 = megaWormRef.current;
        mg2.ang += Math.sin(t / 700) * 0.00006 * dtMs;
        for (let sIdx = mg2.hp; sIdx >= 1; sIdx -= 1) {
          const sd = mg2.dist + (sIdx - 1) * 0.05;
          const wob = Math.sin(t / 260 + sIdx * 1.2) * 0.03;
          const mx2 = C + Math.cos(mg2.ang + wob) * R * sd;
          const my2 = C + Math.sin(mg2.ang + wob) * R * sd;
          const msize = W * (sIdx === 1 ? 0.024 : 0.017) * (1 + 0.12 * Math.sin(t / 180 + sIdx));
          ctx.save();
          ctx.shadowColor = "#ff3b30";
          ctx.shadowBlur = 16;
          ctx.fillStyle = sIdx === 1 ? "rgba(255, 59, 48, 0.98)" : "rgba(200, 30, 25, 0.9)";
          ctx.beginPath();
          ctx.arc(mx2, my2, msize, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        const mhx = C + Math.cos(mg2.ang) * R * mg2.dist;
        const mhy = C + Math.sin(mg2.ang) * R * mg2.dist;
        ctx.fillStyle = "#ffdf6b";
        ctx.font = `900 ${Math.round(W * 0.018)}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText("●".repeat(mg2.hp), mhx, mhy - W * 0.038);
      }
      // defense HQ beacon at the center while the rush is live
      if (def) {
        ctx.beginPath();
        ctx.arc(C, C, W * 0.045 * (1 + 0.15 * Math.sin(t / 120)), 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255, 59, 48, 0.9)";
        ctx.lineWidth = Math.max(1.5, W / 420);
        ctx.shadowColor = "#ff3b30";
        ctx.shadowBlur = 12;
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.fillStyle = "#ff5c7a";
        ctx.font = `900 ${Math.round(W * 0.024)}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText("HQ", C, C + W * 0.075);
      }
      // floating reward / jam texts
      textsRef.current = textsRef.current.filter((tx) => t - tx.born < 1400);
      textsRef.current.forEach((tx) => {
        const age = (t - tx.born) / 1400;
        ctx.globalAlpha = 1 - age;
        ctx.fillStyle = tx.color;
        ctx.font = `900 ${Math.round(W * 0.032)}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText(tx.txt, tx.x, tx.y - age * W * 0.06);
        ctx.globalAlpha = 1;
      });
      ctx.restore();
      ctx.beginPath();
      ctx.arc(C, C, R + W * 0.02, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255, 153, 0, 0.4)";
      ctx.lineWidth = Math.max(2, W / 260);
      ctx.stroke();
      raf = requestAnimationFrame(draw);
    };
    window.addEventListener("pointerdown", unlockRadarAudio);
    // first contacts arrive on a beat, not instantly
    if (!nextSpikeRef.current) nextSpikeRef.current = performance.now() + 2600;
    if (!nextWormRef.current) nextWormRef.current = performance.now() + 9000;
    if (!nextCritterRef.current) nextCritterRef.current = performance.now() + 20000;
    if (!nextMegaRef.current) nextMegaRef.current = performance.now() + 35000;
    // QA handle: read live spike/worm positions and force spawns in tests
    window.__radar = {
      spikes: spikesRef,
      worms: wormsRef,
      critter: critterRef,
      blips: blipsRef,
      fever: feverRef,
      toCss,
      spawnSpike: () => {
        nextSpikeRef.current = 0;
      },
      spawnWorm: () => {
        nextWormRef.current = 0;
      },
      spawnCritter: () => {
        nextCritterRef.current = 0;
      },
      spawnShiny: () => {
        forceShinyRef.current = true;
        nextCritterRef.current = 0;
      },
      spawnMega: () => {
        nextMegaRef.current = 0;
      },
      mega: megaWormRef,
      cry: critterCry,
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointerdown", unlockRadarAudio);
      delete window.__radar;
    };
  }, [exp, collection]);

  return (
    <div className="radar-panel" data-testid="titan-radar">
      <GameFullscreenButton testid="titan-radar-fullscreen" />
      <div className="radar-head">
        {outpostEdit ? (
          <span className="radar-outpost-edit">
            <input
              value={outpostName}
              onChange={(e) => setOutpostName(e.target.value.toUpperCase().slice(0, 18))}
              maxLength={18}
              autoFocus
              data-testid="radar-outpost-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  saveOutpost?.(outpostName.trim() || "HOME RIG");
                  setOutpostEdit(false);
                }
              }}
            />
            <button
              type="button"
              onClick={() => {
                saveOutpost?.(outpostName.trim() || "HOME RIG");
                setOutpostEdit(false);
              }}
              data-testid="radar-outpost-save"
            >
              SAVE
            </button>
          </span>
        ) : (
          <span className="eyebrow">
            {exp.outpost || "HOME RIG"} // LIVE RADAR{" "}
            <button
              type="button"
              className="radar-outpost-edit-btn"
              onClick={() => {
                setOutpostName(exp.outpost || "HOME RIG");
                setOutpostEdit(true);
              }}
              data-testid="radar-outpost-edit"
              aria-label="Rename your outpost"
              title="Name your outpost"
            >
              <Pencil size={11} />
            </button>
          </span>
        )}
        <div className="radar-head-right">
          <button
            type="button"
            className={`radar-ping-toggle ${new Date().getDay() === 0 ? "radar-recap-live" : ""}`}
            onClick={() => {
              const wk = weekKeyNow();
              const w = exp.week && exp.week.key === wk ? exp.week : { caught: 0, megaKills: 0, critters: 0 };
              downloadRecapCard({
                outpost: exp.outpost || "HOME RIG",
                week: wk,
                caught: Math.max(0, (exp.caught || 0) - (w.caught || 0)),
                kills: Math.max(0, (exp.megaKills || 0) - (w.megaKills || 0)),
                critters: Math.max(0, (exp.critters || []).length - (w.critters || 0)),
                streak: exp.daily?.streak || 0,
                defenseBest: exp.defenseBest || 0,
                skinColor: (RADAR_SKINS[exp.skin] || RADAR_SKINS.default).glow,
              });
            }}
            disabled={owned.length === 0}
            data-testid="radar-recap-button"
          >
            RECAP
          </button>
          <button
            type="button"
            className="radar-ping-toggle"
            onClick={() =>
              downloadRadarCard({
                outpost: exp.outpost || "HOME RIG",
                titans: owned.length,
                total: DOLOPALS.length,
                caught: exp.caught || 0,
                defenseBest: exp.defenseBest || 0,
                critters: (exp.critters || []).length,
                critterTotal: CRITTERS.length,
                blips: blipsRef.current,
                skinColor: (RADAR_SKINS[exp.skin] || RADAR_SKINS.default).glow,
              })
            }
            disabled={owned.length === 0}
            data-testid="radar-share-button"
          >
            SHARE
          </button>
          <button
            type="button"
            className={`radar-ping-toggle radar-defense-toggle ${defense && !defense.done ? "is-live" : ""}`}
            onClick={startDefense}
            disabled={owned.length === 0 || Boolean(defense && !defense.done)}
            data-testid="radar-defense-button"
          >
            {defense && !defense.done ? `DEFENSE ${defense.secsLeft}S` : "DEFENSE MODE"}
          </button>
          <button
            type="button"
            className={`radar-ping-toggle ${pingOn ? "" : "is-off"}`}
            onClick={togglePing}
            data-testid="radar-ping-toggle"
          >
            {pingOn ? "PINGS ON" : "PINGS OFF"}
          </button>
          <span className="radar-live">● LIVE</span>
        </div>
      </div>
      {owned.length === 0 ? (
        <p className="radar-empty">NO TITANS ON THE GRID — UNBOX A DROP TO LIGHT UP THE RADAR.</p>
      ) : (
        <>
          <canvas ref={ref} className="radar-canvas" onPointerDown={tapRadar} data-testid="titan-radar-canvas" />
          {defense ? (
            <div className={`radar-defense-bar ${defense.done ? "is-done" : ""}`} data-testid="radar-defense-bar">
              {defense.done ? (
                <>
                  <span>DEFENSE REPORT — SCORE <b data-testid="radar-defense-final">{defense.score}</b> · YOUR BEST <b>{defense.best}</b></span>
                  {defense.record ? <em className="radar-defense-record" data-testid="radar-defense-record">NEW RECORD!</em> : null}
                  <button type="button" onClick={startDefense} data-testid="radar-defense-retry">RUN IT AGAIN</button>
                  <button type="button" onClick={() => setDefense(null)} data-testid="radar-defense-dismiss">CLOSE</button>
                </>
              ) : (
                <span>WAVE RUSH — <b>{defense.secsLeft}S</b> LEFT · SCORE <b data-testid="radar-defense-score">{defense.score}</b> — SQUASH THE FANGS BEFORE THEY CROSS HQ</span>
              )}
            </div>
          ) : null}
          <p className="radar-weekly-champ" data-testid="radar-weekly-champ">
            {weeklyChamp
              ? `WEEKLY DEFENSE CHAMPION — ${weeklyChamp.tag} · ${weeklyChamp.score}`
              : "NO DEFENSE CHAMPION THIS WEEK — THE CROWN IS OPEN"}
          </p>
          <p className="radar-weekly-notice" data-testid="radar-weekly-notice">CRITTER OF THE WEEK — {critterOfTheWeek().name} · SPOTTED FAR MORE OFTEN</p>
          {isBossFriday() ? (
            <p className="radar-boss-friday" data-testid="radar-boss-friday">BOSS FRIDAY — MEGA WORM SURGE LIVE · 2X SPAWNS</p>
          ) : null}
          {isShinySunday() ? (
            <p className="radar-shiny-sunday" data-testid="radar-shiny-sunday">SHINY SUNDAY — GOLD CRITTER ODDS TRIPLED ALL DAY</p>
          ) : null}
          {isBossEve() ? (
            <p className="radar-boss-eve" data-testid="radar-boss-eve">BOSS EVE — MEGA WORM SURGE STARTS AT MIDNIGHT · STOCK UP TODAY</p>
          ) : null}
          <div className="radar-game-hud" data-testid="radar-game-hud">
            <span>SIGNALS <b data-testid="radar-signals-caught">{gameStats.caught}</b></span>
            <span>COMBO <b data-testid="radar-combo">×{gameStats.combo}</b></span>
            <span>BEST <b>×{gameStats.best}</b></span>
            <span>WORMS SCARED <b data-testid="radar-worms-scared">{gameStats.worms}</b></span>
            <span>LIFETIME <b data-testid="radar-lifetime-caught">{exp.caught || 0}</b></span>
            <span>MEGA KILLS <b data-testid="radar-mega-kills">{exp.megaKills || 0}</b></span>
            <span>TREASURE <b data-testid="radar-treasure-state">{exp.treasure === radarDayKey(new Date()) ? "CRACKED" : "BURIED"}</b></span>
            <span>CRITTERS <b data-testid="radar-critters">{(exp.critters || []).length}/{CRITTERS.length}</b></span>
            {feverOn ? <span className="radar-fever-chip" data-testid="radar-fever-chip">FEVER ×2</span> : null}
            {megaOn ? <span className="radar-mega-chip" data-testid="radar-mega-chip">MEGA WORM — HIT IT 3X · +200</span> : null}
          </div>
          <p className="radar-game-hint">TAP SPIKES FOR SCRAP-SHARDS · SCARE RED WORMS BEFORE THEY JAM A RUNNER (+15S)</p>
          <div className="radar-rules">
            <button
              type="button"
              className={`radar-rules-toggle ${rulesOpen ? "is-open" : ""}`}
              onClick={() => setRulesOpen((v) => !v)}
              data-testid="radar-rules-toggle"
            >
              RADAR PROTOCOL // HOW TO PLAY <ChevronDown size={13} />
            </button>
            {rulesOpen ? (
              <div className="radar-rules-grid" data-testid="radar-rules-panel">
                <div>
                  <strong>SIGNAL TAP</strong>
                  <span>Spikes flash on the grid — tap them before their ring fades. SILVER +2 · GOLD +5 · rainbow ULTRA +25 scrap-shards.</span>
                </div>
                <div>
                  <strong>COMBO CHAIN</strong>
                  <span>Back-to-back catches multiply the payout up to ×5. A faded spike breaks the chain.</span>
                </div>
                <div>
                  <strong>WORM CONTACTS</strong>
                  <span>Red fangs crawl toward runners out on missions. Tap to scare one (+1 shard) — if it reaches the zone it jams the clock +15s.</span>
                </div>
                <div>
                  <strong>SIGNAL OF THE DAY</strong>
                  <span>One mission zone glows gold every day and pays 2× shards. Look for the ring and the 2X tag.</span>
                </div>
                <div>
                  <strong>RADAR UPGRADES</strong>
                  <span>Shard Shop tech: WIDER DISH (spikes linger longer), WORM REPELLENT (slower worms), COMBO SHIELD (one free miss per chain).</span>
                </div>
                <div>
                  <strong>CATCHER BADGES</strong>
                  <span>Lifetime catches forge Titan Tag badges at 10, 50 and 100 signals. Linked tags only.</span>
                </div>
                <div>
                  <strong>FEVER MODE</strong>
                  <span>Chain 5 catches and the sweep goes rainbow — ten seconds of double pay on every spike.</span>
                </div>
                <div>
                  <strong>TREASURE PING</strong>
                  <span>One cache is buried on the grid each day. Tap around — ICE COLD to BLAZING — and crack it for +50 shards.</span>
                </div>
                <div>
                  <strong>SCRAP CRITTERS</strong>
                  <span>A green critter wanders in now and then. Tap it to adopt — there are 50 hiding out there, and every species sings its own call. Gold SHINY ones are rare and trade for double. Sundays triple the gold odds.</span>
                </div>
                <div>
                  <strong>YOUR OUTPOST</strong>
                  <span>Pencil icon renames your station, tap a titan blip and it waves back, skins recolor the grid, SHARE downloads your field card.</span>
                </div>
                <div>
                  <strong>MEGA WORM</strong>
                  <span>A segmented red boss surfaces every few minutes. Its body is its health — land three hits before it burrows and take +200 shards. Kills forge Titan Tag badges at 1, 10 and 25.</span>
                </div>
              </div>
            ) : null}
          </div>
          <div className="radar-legend">
            {owned.map((p) => (
              <span key={p.id} className="radar-chip" data-testid={`radar-chip-${p.id}`}>
                <i style={{ background: PAL_COLORS[p.id] || "#ffcf5c" }} />
                {p.name}
                {exp.active[p.id]
                  ? exp.active[p.id].endsAt <= Date.now()
                    ? " · LOOT READY"
                    : " · ON MISSION"
                  : " · IDLE"}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
